/** Блок «В комнате, но нет SKU WB в карточке 1С» под таблицей пополнения (владелец, 15.10.2026).
 *
 *  Товар лежит на складе ФБС или едет туда, а в карточке 1С не заполнен SKU WB (`not_in_meta`):
 *  в расчёт WB он не входит, поэтому в основной таблице его нет — ни в ИТОГО, ни в фильтрах, ни в
 *  «к перемещению». Здесь он только показан (с теми же карточками остатка и «в пути»), чтобы
 *  его не потеряли: если товар продаётся на WB — заполнить реквизит. Ввода количества нет.
 */
import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";

import type * as api from "@/api/supply";
import type { FbsCardCtx } from "./fbsCards";
import { DANGER_CLASS, stockNegative } from "./fbsHighlight";
import { StockCell, TransitCell } from "./FbsInfoCards";

export function FbsNoWbCardBlock({
  rows,
  card,
}: {
  rows: api.FbsNoWbCardRow[];
  card: FbsCardCtx;
}): React.ReactElement | null {
  const [open, setOpen] = useState(false);
  if (rows.length === 0) return null;
  return (
    <div className="rounded-md border border-dashed bg-card px-3 py-2.5 text-sm" data-testid="fbs-no-wb-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 text-left font-medium"
      >
        {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        В комнате, но нет SKU WB в карточке 1С ({rows.length})
      </button>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Товар лежит на складе ФБС или едет туда, но в карточке 1С не заполнен SKU WB — в расчёт WB
        не входит; если товар продаётся на WB — заполните реквизит.
      </p>

      {open && (
        <div className="mt-2 max-h-[50vh] overflow-auto rounded-md border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 border-b bg-background text-muted-foreground [&>tr]:bg-muted/50">
              <tr>
                <th className="px-2 py-1 text-left font-medium">Артикул</th>
                <th className="px-2 py-1 text-left font-medium">Товар</th>
                <th className="px-2 py-1 text-right font-medium">Остаток ФБС</th>
                <th className="px-2 py-1 text-right font-medium">В пути</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.nom_key} className="border-b last:border-0 hover:bg-accent/60">
                  <td className="px-2 py-1">
                    {r.article || <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="max-w-0 px-2 py-1">
                    <span className="block truncate" title={r.nom_name || r.nom_key}>
                      {r.nom_name || (
                        <span className="text-muted-foreground">нет в справочнике 1С: {r.nom_key}</span>
                      )}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">
                    <StockCell
                      row={r}
                      ctx={card}
                      valueClassName={stockNegative(r) ? DANGER_CLASS : undefined}
                    />
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">
                    <TransitCell row={r} ctx={card} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
